import React, { useState, useEffect, useCallback, useRef } from 'react';
import ApplicantDetailsForm from '../components/ApplicantDetailsForm';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getServiceById, submitCase, updateProfile } from '../services/api';
import FormRenderer from '../components/FormRenderer';
import ProgressNavigator from '../components/ProgressNavigator';
import { useForm } from '../context/FormContext';
import FlowersServicePage from './FlowersServicePage';
import Spinner from '../components/Spinner';
import './ApplyPage.css';

function ApplyPage({ caseId }) {
    const { serviceId } = useParams();
    const formRendererRef = useRef(null);
    console.log("caseids", caseId);
    const navigate = useNavigate();
    const { formData, updateFormData } = useForm();

    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);

    // Check URL params for initial step
    const [searchParams] = useSearchParams();
    const initialStep = searchParams.get('step');
    const [showHtmlForm, setShowHtmlForm] = useState(initialStep !== 'form');

    const [applicantDetailsData, setApplicantDetailsData] = useState({});
    const [isProceeding, setIsProceeding] = useState(false);

    useEffect(() => {
        const fetchServiceDetails = async () => {
            setLoading(true);
            setError(null);
            setSubmissionError(null);
            try {
                const response = await getServiceById(serviceId);
                setService(response.data);
            } catch (err) {
                console.error(`Error fetching service ${serviceId}:`, err);
                setError(err.response?.data?.message || err.message || 'Failed to fetch service details');
            } finally {
                setLoading(false);
            }
        };

        if (serviceId) {
            fetchServiceDetails();
        } else {
            setError("No Service ID provided.");
            setLoading(false);
        }
    }, [serviceId]);

    const handleFormChange = useCallback((data) => {
        updateFormData(data);
    }, [updateFormData]);

    const handleFormSubmit = useCallback(async (data) => {
        if (!serviceId) return;

        setIsSubmitting(true);
        setSubmissionError(null);

        const mergedData = { ...applicantDetailsData, ...data };
        updateFormData(mergedData);

        try {
            const response = await submitCase(serviceId, caseId, mergedData);
            const newCaseId = response.data?.caseId;

            if (newCaseId) {
                console.log("Case submitted successfully, Case ID:", newCaseId);
                navigate(`/apply/${serviceId}/documents?caseId=${newCaseId}`);
            } else {
                console.error("Backend did not return a caseId after submission.");
                setSubmissionError({ _general: 'Case created, but failed to proceed to document upload. Please contact support.' });
                setIsSubmitting(false);
            }

        } catch (err) {
            console.error("Error submitting case:", err);
            const apiErrors = err.response?.data?.errors;
            if (apiErrors && typeof apiErrors === 'object' && Object.keys(apiErrors).length > 0) {
                setSubmissionError(apiErrors);
            } else {
                setSubmissionError({ _general: err.response?.data?.message || err.message || 'Submission failed.' });
            }
            setIsSubmitting(false);
        }
    }, [serviceId, navigate, updateFormData]);

    if (loading) {
        return <div className="apply-page-container"><p>Loading service details...</p></div>;
    }

    if (error) {
        return <div className="apply-page-container"><p style={{ color: 'red' }}>Error: {error}</p></div>;
    }

    if (!service || !service.formSchema) {
        return <div className="apply-page-container"><p style={{ color: 'orange' }}>Service data or form schema is missing.</p></div>;
    }

    if (showHtmlForm) {
        return (
            <ApplicantDetailsForm
                initialData={applicantDetailsData}
                onSave={setApplicantDetailsData}
                isProceeding={isProceeding}
                onProceed={async (data) => {
                    setIsProceeding(true);
                    try {
                        await updateProfile(data);
                        setApplicantDetailsData(data);
                        setShowHtmlForm(false);
                    } catch (error) {
                        console.error("Error updating profile:", error);
                    } finally {
                        setIsProceeding(false);
                    }
                }}
            />
        );
    }

    // Check for Flowers Service ID
    if (serviceId === '3e05d6ef-473d-4c2a-9fd5-747c8beef595') {
        return <FlowersServicePage caseId={caseId} serviceId={serviceId} />;
    }

    return (
        <div className="apply-page-container">
            <aside className="apply-sidebar">
                <ProgressNavigator currentStepKey="form" />
            </aside>
            <main className="apply-main-content">
                <div className="apply-form-header">
                    <span className="apply-form-kicker">e-Service Application</span>
                    <h1>{service.name}</h1>
                    {service.description && <p className="apply-form-description">{service.description}</p>}
                    <p className="apply-form-note">
                        Fields marked <span style={{ color: '#c0392b' }}>*</span> are required.
                        Your progress is kept while you stay on this page.
                    </p>
                </div>
                <div className="form-actions-wrapper">
                    <FormRenderer
                        ref={formRendererRef}
                        schema={service.formSchema}
                        data={formData}
                        onSubmit={handleFormSubmit}
                        onChange={handleFormChange}
                        readOnly={isSubmitting}
                        externalErrors={submissionError}
                        showSubmitButton={false}
                    />
                    <div className="form-navigation-buttons">
                        <button type="button" onClick={() => setShowHtmlForm(true)} className="previous-button">
                            Previous
                        </button>
                        <button
                            type="button"
                            onClick={() => formRendererRef.current?.submit()}
                            className="submit-application-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? <Spinner /> : 'Submit Application'}
                        </button>
                    </div>
                </div>

                {submissionError?._general && (
                    <p className="apply-form-error">
                        <strong>Submission Error:</strong> {submissionError._general}
                    </p>
                )}
            </main>
        </div>
    );
}

export default ApplyPage;